// PLAN.md §9 M3 — bottom-right live PnL leaderboard.
// Top 10 by PnL = (bankroll + active_stake) - starting_capital.
// PLAN.md §10 risk #6: ranking by PnL (not bankroll) keeps welcome-bonus
// hoarders at PnL=0, mitigating sybil farming.

import { useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { useStore } from '../store.js'
import { DeltaNumber } from './ported/index.js'

const TOP_N = 10

type Row = {
  id: string
  handle: string
  total: number
  pnl: number
  pnlPct: number
  isMe: boolean
}

export function Leaderboard() {
  const players = useStore((s) => s.players)
  const selfId = useStore((s) => s.self?.player_id)

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = []
    for (const p of players.values()) {
      const total = p.bankroll + (p.stake > 0 ? p.stake : 0)
      const pnl = total - p.starting_capital
      const pnlPct = p.starting_capital > 0 ? (pnl / p.starting_capital) * 100 : 0
      out.push({
        id: p.id,
        handle: p.handle,
        total,
        pnl,
        pnlPct,
        isMe: p.id === selfId,
      })
    }
    out.sort((a, b) => b.pnl - a.pnl)
    return out.slice(0, TOP_N)
  }, [players, selfId])

  if (rows.length === 0) return null

  return (
    <aside className="fixed bottom-4 right-4 w-[280px] bg-white/95 border border-light-grey rounded-xl shadow-big p-3 z-40 backdrop-blur">
      <header className="flex items-baseline justify-between pb-2 mb-1 border-b border-light-grey">
        <span className="text-label text-grey uppercase tracking-wider">Leaderboard</span>
        <span className="text-label text-grey tabular-nums">PnL</span>
      </header>
      <ol className="flex flex-col gap-0.5">
        <AnimatePresence initial={false}>
          {rows.map((r, i) => (
            <motion.li
              key={r.id}
              layout
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ type: 'spring', stiffness: 500, damping: 36 }}
              className={`flex items-center justify-between gap-2 px-2 py-1 rounded ${
                r.isMe ? 'bg-black/5' : ''
              }`}
            >
              <span className="text-label tabular-nums text-grey w-5 shrink-0">{i + 1}</span>
              <span
                className={`text-label flex-1 truncate ${
                  r.isMe ? 'text-black font-semibold' : 'text-black'
                }`}
                title={r.handle}
              >
                {r.handle}
              </span>
              <DeltaNumber value={r.pnl} format="usd" size="sm" precision={2} />
            </motion.li>
          ))}
        </AnimatePresence>
      </ol>
    </aside>
  )
}
